import concat from "lodash/fp/concat";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import has from "lodash/fp/has";
import head from "lodash/fp/head";
import isNil from "lodash/fp/isNil";
import isArray from "lodash/fp/isArray";
import keyBy from "lodash/fp/keyBy";
import map from "lodash/fp/map";
import mapValues from "lodash/fp/mapValues";
import merge from "lodash/fp/merge";
import negate from "lodash/fp/negate";
import reduce from "lodash/fp/reduce";
import uniq from "lodash/fp/uniq";
import values from "lodash/fp/values";

import { getFilteredOptions, getFlatFields, shouldBeDisplayed } from "utils";

const filterWithKey = filter.convert({ cap: false });

function generateDefaultValues(userChoiceValues = {}) {
  return (fieldsWithDefaultValues) => {
    const fieldsWithKnownDefaultValues = filter(
      flow(get("defaultValue"), negate(isArray))
    )(fieldsWithDefaultValues);

    const nextUserChoiceValues = flow(
      keyBy("name"),
      mapValues(get("defaultValue")),
      (v) => merge(v)(userChoiceValues)
    )(fieldsWithKnownDefaultValues);

    const fieldsWithMultipleDefaultValues = filter(
      flow(get("defaultValue"), isArray)
    )(fieldsWithDefaultValues);

    if (fieldsWithMultipleDefaultValues.length === 0) {
      return fieldsWithKnownDefaultValues;
    }

    const field = fieldsWithMultipleDefaultValues.shift();

    const availableOption = flow(
      getOr([], "defaultValue"),
      map((value) => field.options.find((option) => option.value === value)),
      filter(negate(isNil)),
      (options) =>
        getFilteredOptions({
          options,
          userChoiceValues: nextUserChoiceValues,
        }),
      head
    )(field);

    const nextFieldsWithDefaultValues = [
      ...fieldsWithKnownDefaultValues,
      ...fieldsWithMultipleDefaultValues,
      {
        ...field,
        defaultValue: availableOption?.value,
      },
    ];

    return generateDefaultValues(nextUserChoiceValues)(
      nextFieldsWithDefaultValues
    );
  };
}

export const getDefaultValues = ({
  product,
  userChoiceValuesToUpdate = {},
}) => {
  return flow(
    getOr([], ["customisations"]),
    getFlatFields,
    filter(has("defaultValue")),
    // split default values into set where the value is known and where the value is an array of possible values
    filter(shouldBeDisplayed(userChoiceValuesToUpdate)),
    generateDefaultValues(userChoiceValuesToUpdate),
    keyBy("name"),
    mapValues(get("defaultValue")),
    (d) => merge(d)(userChoiceValuesToUpdate)
  )(product);
};

export default function getSelectedProductStateValues({
  hasVolumentalData = false,
  initialUserChoiceValues = {},
} = {}) {
  return (product = {}) => {
    const customisations = getOr([], ["customisations"])(product);

    const defaultValues = getDefaultValues({
      product,
      userChoiceValuesToUpdate: {
        ...(product?.initialUserChoiceValues || {}),
        ...initialUserChoiceValues,
      },
    });

    const selectedCustomisationId = flow(
      filter(negate(has("preventRender"))),
      filter(
        ({ hideIfNoVolumentalData }) =>
          hasVolumentalData || !hideIfNoVolumentalData
      ),
      head,
      getOr("", "id")
    )(customisations);

    const customisationsVisited = flow(
      reduce((acc, { id, fields }) => {
        return {
          ...acc,
          ...reduce(
            (a, { name }) => ({
              ...a,
              [name]: id,
            }),
            {}
          )(fields),
        };
      }, {}),
      filterWithKey((customisationId, fieldName) =>
        has(fieldName)(initialUserChoiceValues)
      ),
      values,
      concat(selectedCustomisationId),
      uniq
    )(customisations);

    // DEV - hard fix, should be checking for empty fields
    if (
      initialUserChoiceValues["retro-design"] &&
      initialUserChoiceValues["retro-design"] !== "unique-design"
    ) {
      customisationsVisited.push("DigiPrintGoalieGear2024__color");
    }

    return {
      userChoiceValues: {
        ...defaultValues,
        ...(product?.initialUserChoiceValues || {}),
        ...initialUserChoiceValues,
      },

      selectedProductId: product?.id || "",

      // select first customisation
      selectedCustomisationId,

      customisationsVisited,

      fieldsTouched: Object.keys(initialUserChoiceValues),

      // select first image angle
      selectedImageAngle:
        product?.defaultImageAngle ||
        Object.keys(product?.images || {})[0] ||
        "",
    };
  };
}
